// One document keeps level scaling and stat caps on the same revision.
export const BALANCE_DATA_KEY = "EC2_BALANCE_DATA_V1";
export const LEVEL_FIELDS = ["expTable", "enemyExpTable", "enemyHpTable", "enemyDamageTable", "enemyGoldTable", "enemyArmorTable"] as const;
export const STAT_NAMES = ["Max HP", "Max MP", "Att. Damage", "Defense", "Crit. Rate", "Crit. Dmg", "Att.Speed", "Speciality", "Evasion", "Recovery", "Cooldown Red", "MP Cost Red", "Phy Res", "Ele Res", "HP Percent", "Basic Atk Dmg", "Skill Atk Dmg", "Accuracy", "Mana Gain", "SP Regen", "Consumable Plus"] as const;
const STAT_FIELDS = ["baseValue", "softCap", "hardCap", "minSocketValue", "maxSocketValue"] as const;
type StatData = { statName: string } & Record<typeof STAT_FIELDS[number], number>;
export type BalanceData = { schemaVersion: 1; statData: StatData[] } & Record<typeof LEVEL_FIELDS[number], number[]>;

export function validateBalanceData(value: unknown): string[] {
  const errors: string[] = [];
  if (!value || typeof value !== "object" || Array.isArray(value)) return ["Expected a balance document"];
  const data = value as Record<string, unknown>;
  if (data.schemaVersion !== 1) errors.push("Unsupported schemaVersion");
  const allowed = new Set<string>(["schemaVersion", "statData", ...LEVEL_FIELDS]);
  if (Object.keys(data).some(key => !allowed.has(key))) errors.push("Unknown document field");
  const count = Array.isArray(data.expTable) ? data.expTable.length : 0;
  if (count < 2 || count > 1000) errors.push("Expected 2–1000 level rows, including the terminal EXP row");
  for (const field of LEVEL_FIELDS) {
    const rows = data[field];
    if (!Array.isArray(rows) || rows.length !== count || rows.some(n => typeof n !== "number" || !Number.isFinite(n) || n < 0 || n > 2147483520)) {
      errors.push(`${field}: expected equal-length arrays of nonnegative, Unity int-compatible numbers`);
    }
  }
  if (!Array.isArray(data.statData) || data.statData.length !== STAT_NAMES.length) {
    errors.push("Expected exactly 21 ordered stat entries");
  } else {
    data.statData.forEach((raw, i) => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) { errors.push(`Invalid stat ${i}`); return; }
      const row = raw as Record<string, unknown>;
      if (row.statName !== STAT_NAMES[i]) errors.push(`Stat ${i} must be ${STAT_NAMES[i]}`);
      if (Object.keys(row).some(key => key !== "statName" && !(STAT_FIELDS as readonly string[]).includes(key))) errors.push(`Unknown stat field at ${i}`);
      for (const field of STAT_FIELDS) {
        const n = row[field];
        if (typeof n !== "number" || !Number.isFinite(n) || (n !== -1 && n < 0) || n > 2147483520) errors.push(`Invalid ${field} at stat ${i}`);
      }
      for (const [low, high] of [["softCap", "hardCap"], ["minSocketValue", "maxSocketValue"]] as const) {
        if (typeof row[low] === "number" && typeof row[high] === "number" && row[low] !== -1 && row[high] !== -1 && row[low] > row[high]) errors.push(`${low} exceeds ${high} at stat ${i}`);
      }
    });
  }
  return errors;
}
